/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';
import { MenuListComponentProps } from 'react-select/src/components/Menu';
import axios from 'axios';
import set from 'lodash/set';
import get from 'lodash/get';
import { isExperienceEditorActive } from '@sitecore-jss/sitecore-jss-react';
import { v4 as uuidv4 } from 'uuid';

import config from 'jss-boilerplate/temp/config.js';
import {
	RetrieveAddressResponseDataType,
	AddressDataType,
} from 'core/api/client/SystemValidatorApi';
import { doRefreshToken } from 'core/utils/api-utils';
import { getAccessToken } from 'core/browserStorage';
import { AKAMAI_BOT_FLAG, X_CORRELATION_APP } from 'core/constants';
import { ADDRESS } from 'core/constants/form';
import GetAddressMock from 'core/hooks/useGetAddressFields/api/mocks/GetAddress.mock.json';
import { isJssField } from 'core/utils/sitecore-utils';
import IProps from 'pods/forms/components/formFieldProps.d';
import UpdatedCtrlHolder from 'pods/forms/components/structural/UpdatedCtrlHolder';
import { isStorybook } from 'stories/utils';

import * as S from './AddressAutocomplete.style';
import {
	MANUAL_ADDRESS,
	MANUAL_OPTION,
	SB_FAIL,
	SEARCH_RESULTS_MAX_DISPLAY,
	SEARCH_TEXT_TRIGGER_LENGTH,
} from './AddressAutocomplete.const';

// API base config
const apiConfig = config;
const systemValidationBase = `${apiConfig.australianSuperApi?.apiDomain}${apiConfig.australianSuperApi?.apiPathSystemValidation}`;

interface IAddressOption {
	label: string;
	value: string;
	full_address?: string;
	address?: AddressDataType;
	customLabel?: JSX.Element;
}

const myOptionLabel = ({ label, customLabel }: IAddressOption) => {
	if (!!customLabel) {
		return customLabel;
	} else {
		return <>{label}</>;
	}
};

const MenuList = (props: MenuListComponentProps<any>) => {
	return <S.MenuList {...props}>{props.children}</S.MenuList>;
};

const AddressAutocompleteAxios = ({
	config,
	computed,
	htmlId,
	value,
	handleChange,
	getDisplayError,
	renderTextNode,
	ctrlHolderProps,
	path,
	setValues,
	formValues,
}: IProps) => {
	const [showOptions, setShowOptions] = useState<boolean>(false);

	const { data = {}, appendMsg, bottomMsg } = config;
	const {
		placeholder,
		labelHelpText,
		inputAttrs = {},
		fullWidth,

		// Note: Specific for T19 Withdrawals - SMSF Transfer - Postal Address
		isWithdrawalPostalAddress,
		withdrawalPostalAddressPath,
	} = data;

	const label = isJssField(data.label)
		? data.label
		: renderTextNode(data.label, 'span');
	const helpText = isJssField(labelHelpText)
		? labelHelpText
		: renderTextNode(labelHelpText, 'span');
	const tooltip = renderTextNode(data.tooltip);
	const errorMsg = getDisplayError();
	const hasError = !!errorMsg;
	const { required, disabled } = computed;

	const _ctrlHolderProps = {
		htmlId,
		label,
		helpText,
		errorMsg,
		required,
		appendMsg,
		value,
		disabled,
		bottomMsg,
		placeholder,
		type: inputAttrs?.type,
		fullWidth: data.fullWidth,
		...(data.tooltip && { tooltip }),
		...ctrlHolderProps,
	};

	const addressLookupPathPrefix = path.split('.')[0];

	// Note: Can be from saved forms or payload back/edit
	const storedAddressValue = get(formValues, addressLookupPathPrefix);

	const asyncSelectDefaultValue = () => {
		/**
		 * Note: See notes on onChange else
		 * Since we are storing the value as MANUAL_ADDRESS inside the path value
		 * then we can just change it back to MANUAL_OPTION to display it when
		 * navigating back.
		 */
		return storedAddressValue
			? storedAddressValue === MANUAL_ADDRESS
				? MANUAL_OPTION
				: {
						label: storedAddressValue?.label,
						value: storedAddressValue?.value,
						customLabel: storedAddressValue?.addressValue,
				  }
			: null;
	};

	const onInputChange = (searchText: string) => {
		// Note: Show only option dropdown on
		if (searchText?.length >= SEARCH_TEXT_TRIGGER_LENGTH) {
			setShowOptions(true);
		} else {
			setShowOptions(false);
		}
	};

	const onChange = (option: IAddressOption) => {
		if (option?.value !== MANUAL_ADDRESS) {
			const {
				subdwelling_name = '',
				street_number = '',
				street_name = '',
				street_type = '',
				suburb = '',
				state = '',
				postcode = '',
			} = option?.address ?? {};

			// Note: Specific for T19 Withdrawals - SMSF Transfer - Postal Address
			if (isWithdrawalPostalAddress) {
				// Note: Set address-autocomplete value
				set(formValues, addressLookupPathPrefix, option);

				// Note: Set postal_address object values
				const postalAddressSetter = (variablePath: string, value: string) => {
					const cleanPath =
						withdrawalPostalAddressPath ?? addressLookupPathPrefix;
					set(formValues, `${cleanPath}.${variablePath}`, value);
				};

				postalAddressSetter(
					ADDRESS.STREET_1,
					`${subdwelling_name} ${street_number} ${street_name} ${street_type}`,
				);
				postalAddressSetter(ADDRESS.STREET_2, '');
				postalAddressSetter(ADDRESS.SUBURB, suburb ?? 'N/A');
				postalAddressSetter(ADDRESS.SUBURB_CITY, suburb ?? 'N/A');
				postalAddressSetter(ADDRESS.STATE, state);
				postalAddressSetter(ADDRESS.POSTCODE, postcode);

				setTimeout(() => {
					setValues(formValues);
				});
			}
		} else {
			/**
			 * Note: Manual is not saved as an option object for the address lookup path
			 * because it is used in the rules to show/hide.
			 * The `shown` rule is using includes which can't compare objects.
			 * This means that the rule should use MANUAL_ADDRESS as the value to compare to.
			 */
		}

		handleChange(option?.value);
	};

	const _loadAutocompleteOptions = (searchText: string) => {
		if (searchText?.length >= SEARCH_TEXT_TRIGGER_LENGTH) {
			if (isStorybook()) {
				return new Promise(resolve => {
					setTimeout(() => {
						if (searchText?.toLowerCase() === SB_FAIL) {
							resolve([MANUAL_OPTION]);
						} else {
							const rawAddressData = GetAddressMock?.data?.addresses ?? [];
							const addressses = rawAddressData?.slice(
								0,
								SEARCH_RESULTS_MAX_DISPLAY,
							);

							const addressOptions: IAddressOption[] = addressses?.map(
								address => ({
									label: address.full_address,
									value: address.full_address,
									address: address.address as any, // TODO
								}),
							);
							addressOptions?.push(MANUAL_OPTION);

							resolve(addressOptions);
						}
					}, 2000);
				});
			} else {
				const addressLookUpParam = {
					full_address: searchText,
				};

				return new Promise(resolve => {
					setTimeout(async () => {
						// Check if we need to do a refreshtoken for the case when the user has been
						// staying in the form for too long
						if (!isStorybook() || !isExperienceEditorActive())
							await doRefreshToken();

						return axios({
							url: `${systemValidationBase}/validators/address-lookup`,
							params: addressLookUpParam,
							method: 'GET',
							responseType: 'json',
							headers: {
								'Authorization': `Bearer ${getAccessToken()}`,
								'Accept': 'application/json',
								'Cache-Control': 'no-cache',
								'X-API-Key': apiConfig?.externalServices?.coarseGrainApiKey,
								'X-Correlation-Id': uuidv4(), // FED initiated correlationID
								'X-Correlation-App': X_CORRELATION_APP, // Link detection of request origin
							},
							withCredentials: AKAMAI_BOT_FLAG,
						})
							.then(response => {
								const rawAddressData = response?.data?.data?.addresses ?? [];
								const addressses = rawAddressData?.slice(
									0,
									SEARCH_RESULTS_MAX_DISPLAY,
								);

								const addressOptions: IAddressOption[] = addressses?.map(
									(address: RetrieveAddressResponseDataType) => ({
										label: address.full_address,
										value: address.full_address,
										address: address.address as string,
									}),
								);

								addressOptions?.push(MANUAL_OPTION);

								resolve(addressOptions);
							})
							.catch(() => {
								resolve([MANUAL_OPTION]);
							});
					}, 2000);
				});
			}
		}
	};

	return (
		<UpdatedCtrlHolder {..._ctrlHolderProps}>
			<S.AddressAutocompleteContainer {...{ disabled, hasError, fullWidth }}>
				<AsyncSelect
					defaultValue={asyncSelectDefaultValue()}
					placeholder={placeholder}
					className="formBuilder__addressAutocomplete"
					classNamePrefix="formBuilder__addressAutocomplete"
					isDisabled={disabled}
					cacheOptions
					loadOptions={_loadAutocompleteOptions}
					onChange={onChange as any}
					onInputChange={onInputChange}
					components={{
						MenuList,
						DropdownIndicator: null,
					}}
					formatOptionLabel={myOptionLabel}
					isClearable
					openMenuOnFocus={false}
					openMenuOnClick={false}
					noOptionsMessage={(input: any) =>
						input.inputValue.length >= 5 ? 'No results' : null
					}
					aria-label="Select"
					menuPortalTarget={document.body}
					menuPosition="fixed"
					menuIsOpen={showOptions}
				/>
			</S.AddressAutocompleteContainer>
		</UpdatedCtrlHolder>
	);
};

export default AddressAutocompleteAxios;
